const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

class ApiService {
  constructor() {
    this.base = API_BASE;
  }

  getToken() {
    return localStorage.getItem("fh_token");
  }

  setTokens(accessToken, refreshToken) {
    localStorage.setItem("fh_token", accessToken);
    if (refreshToken) localStorage.setItem("fh_refresh", refreshToken);
  }

  clearTokens() {
    localStorage.removeItem("fh_token");
    localStorage.removeItem("fh_refresh");
  }

  async request(endpoint, options = {}) {
    const url = `${this.base}${endpoint}`;
    const headers = { ...options.headers };

    if (!(options.body instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    const token = this.getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const res = await fetch(url, { ...options, headers });

    if (res.status === 401 && token) {
      const refreshed = await this.tryRefresh();
      if (refreshed) {
        headers["Authorization"] = `Bearer ${this.getToken()}`;
        const retry = await fetch(url, { ...options, headers });
        return this.handleResponse(retry);
      }
      this.clearTokens();
      window.location.href = "/login";
      throw new Error("Session expired");
    }

    return this.handleResponse(res);
  }

  async handleResponse(res) {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw { status: res.status, ...data };
    return data;
  }

  async tryRefresh() {
    try {
      const refreshToken = localStorage.getItem("fh_refresh");
      if (!refreshToken) return false;
      const res = await fetch(`${this.base}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      this.setTokens(data.accessToken, data.refreshToken);
      return true;
    } catch {
      return false;
    }
  }

  // ─── Auth ───
  register(data) {
    return this.request("/auth/register", { method: "POST", body: JSON.stringify(data) });
  }
  login(data) {
    return this.request("/auth/login", { method: "POST", body: JSON.stringify(data) });
  }
  forgotPassword(email) {
    return this.request("/auth/forgot-password", { method: "POST", body: JSON.stringify({ email }) });
  }
  resetPassword(token, password) {
    return this.request("/auth/reset-password", { method: "POST", body: JSON.stringify({ token, password }) });
  }
  getMe() {
    return this.request("/auth/me");
  }
  logout() {
    const refreshToken = localStorage.getItem("fh_refresh");
    return this.request("/auth/logout", { method: "POST", body: JSON.stringify({ refreshToken }) }).finally(() => this.clearTokens());
  }

  // ─── Posts ───
  getPosts(page = 1, limit = 10) {
    return this.request(`/posts?page=${page}&limit=${limit}`);
  }
  getPost(id) {
    return this.request(`/posts/${id}`);
  }
  createPost(formData) {
    return this.request("/posts", { method: "POST", body: formData });
  }
  deletePost(id) {
    return this.request(`/posts/${id}`, { method: "DELETE" });
  }
  toggleBless(postId) {
    return this.request(`/posts/${postId}/bless`, { method: "POST" });
  }

  // ─── Comments ───
  getComments(postId) {
    return this.request(`/posts/${postId}/comments`);
  }
  createComment(postId, content, parentId) {
    return this.request(`/posts/${postId}/comments`, { method: "POST", body: JSON.stringify({ content, parentId }) });
  }
  deleteComment(postId, commentId) {
    return this.request(`/posts/${postId}/comments/${commentId}`, { method: "DELETE" });
  }

  // ─── Plans ───
  getPlans() {
    return this.request("/plans");
  }
  getMySubscription() {
    return this.request("/plans/my-subscription");
  }
  subscribe(planId) {
    return this.request(`/plans/${planId}/subscribe`, { method: "POST" });
  }
  getSuggestedMentors(page = 1, limit = 6) {
    return this.request(`/users/mentor-suggested?page=${page}&limit=${limit}`);
  }
  // ─── Media (FaithStream) ───
  getMedia(category = "all") {
    return this.request(`/media?category=${category}`);
  }
  getMyPost(page) {
    return this.request(`/posts/user?page=${page}&limit=10`);
  }
  getPostsByUser(user_id, page) {
    return this.request(`/posts/other-user/${user_id}?page=${page}`);
  }
  getMessageHistory(user_id, page) {
    return this.request(`/message`);
  }
  getMessageHistorySpecificUser(user_id, page) {
    return this.request(`/message/history/${user_id}?page=${page}`);
  }
  getLiveRoomMessages(room_id, page) {
    return this.request(`/live_room/messages/${room_id}?page=${page}`);
  }
  // ─── Users ───
  getUser(id) {
    return this.request(`/users/info/${id}`);
  }
  sendPrivateChatMessage(formData) {
    return this.request("/message/send", { method: "POST", body: formData });
  }

  sendRoomChatMessage(roomId, formData) {
    return this.request(`/live_room/send-message/${roomId}`, { method: "POST", body: formData });
  }
  getSuggestedUsers() {
    return this.request("/users/suggested");
  }
  updateProfile(formData) {
    return this.request("/users/profile", { method: "PUT", body: formData });
  }
  updateUserInterest(data) {
    return this.request("/users/interest-tags", { method: "PUT", body: JSON.stringify(data) });
  }
  updateUserRole(role) {
    return this.request(`/users/role?value=${role}`, { method: "PUT" });
  }
  toggleFollow(userId) {
    return this.request(`/users/${userId}/follow`, { method: "POST" });
  }
  getUserDataList(page, search) {
    return this.request(`/users/list?page=${page}&search=${search}`, { method: "GET" });
  }
  getModerationItemList(page) {
    return this.request(`/moderation/list?page=${page}`, { method: "GET" });
  }
  removeModerationItem(id) {
    return this.request(`/moderation/${id}/remove`, { method: "PUT" });
  }
  suspendUserModerationItem(id) {
    return this.request(`/moderation/${id}/suspend`, { method: "PUT" });
  }
  unsuspendUserModerationItem(id) {
    return this.request(`/moderation/${id}/unsuspend`, { method: "PUT" });
  }
  approveModerationItem(id) {
    return this.request(`/moderation/${id}/approve`, { method: "PUT" });
  }
  // Example:
  getAllPosts(page) {
    return this.request(`/posts/list?page=${page}`, { method: "GET" });
  }
  getAllComments(page) {
    return this.request(`/posts/comment/list?page=${page}`, { method: "GET" });
  }
  getUserList(page, search) {
    return this.request(`/users/list?page=${page}&search=${search}`, { method: "GET" });
  }
  toggleSuspendUser(userID) {
    return this.request(`/users/toggleUserSuspension/${userID}`, { method: "POST" });
  }
  deleteUser(userID) {
    return this.request(`/users/delete/${userID}`, { method: "DELETE" });
  }

  getPayments(page) {
    return this.request(`/plans/payments?page=${page}`, { method: "GET" });
  }

  getDashboardAdminData() {
    return this.request(`/dashboard/admin`, { method: "GET" });
  }
  getStreamCategories(page = 1, limit = 10, search = "") {
    return this.request(`/stream-category/list?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`, { method: "GET" });
  }
  getStreamCategoriesData() {
    return this.request(`/stream-category`, { method: "GET" });
  }
  createStreamCategory(payload) {
    return this.request("/stream-category", { method: "POST", body: JSON.stringify(payload) });
  }
  updateStreamCategory(id, payload) {
    return this.request(`/stream-category/${id}`, { method: "PUT", body: JSON.stringify(payload) });
  }

  deleteStreamCategory(id) {
    return this.request(`/stream-category/${id}`, { method: "DELETE" });
  }
  toggleStreamCategory(id) {
    return this.request(`/stream-category/toggleStatus/${id}`, { method: "PUT" });
  }
  getCategoriesList(page = 1, limit = 10) {
    return this.request(`/stream-category`);
  }
  // ─── Faith Stream Media ───
  getMediaItems(page = 1, limit = 10, search = "", category = "all") {
    return this.request(`/media/list?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}&category=${category}`);
  }
  getSavedStreamMedia(page = 1, limit = 10) {
    return this.request(`/saved_stream/my?page=${page}&limit=${limit}`, { method: "GET" });
  }
  toggleSaveStreamMedia(stream_id) {
    return this.request(`/saved_stream/toggle/${stream_id}`, { method: "PUT" });
  }
  createMediaItem(formData) {
    return this.request("/media", { method: "POST", body: formData });
  }
  updateMediaItem(id, formData) {
    return this.request(`/media/${id}`, { method: "PUT", body: formData });
  }
  deleteMediaItem(id) {
    return this.request(`/media/${id}`, { method: "DELETE" });
  }
  // ─── Settings ───
  getSettings() {
    return this.request("/setting");
  }
  updateSettings(data) {
    return this.request("/setting", { method: "PUT", body: JSON.stringify(data) });
  }

  // ─── Challenges ───
  getChallenges(page = 1, search = "") {
    return this.request(`/challenge?page=${page}&search=${encodeURIComponent(search)}`);
  }
  createChallenge(data) {
    return this.request("/challenge", { method: "POST", body: JSON.stringify(data) });
  }
  updateChallenge(id, data) {
    return this.request(`/challenge/${id}`, { method: "PUT", body: JSON.stringify(data) });
  }
  toggleChallengeStatus(id) {
    return this.request(`/challenge/toggleStatus/${id}`, { method: "PUT" });
  }
  deleteChallenge(id) {
    return this.request(`/challenge/${id}`, { method: "DELETE" });
  }
  fetchAdminNotifications(page = 1, limit = 10) {
    return this.request(`/notification/admin?page=${page}&limit=${limit}`);
  }

  fetchNotifications(page = 1, limit = 10) {
    return this.request(`/notification/user?page=${page}&limit=${limit}`);
  }
  markUserNotificationRead(id) {
    return this.request(`/notification/user/read/${id}`, { method: "PUT" });
  }
  markAllUserNotificationsRead() {
    return this.request(`/notification/user/all_read`, { method: "PUT" });
  }
  markNotificationRead(id) {
    return this.request(`/notification/admin/read/${id}`, { method: "PUT" });
  }
  markAllNotificationsRead() {
    return this.request(`/notification/admin/all_read`, { method: "PUT" });
  }
  joinChallenge(challenge_id) {
    return this.request(`/challenge/join/${challenge_id}`, { method: "POST" });
  }
  getCurrentChallenge() {
    return this.request(`/challenge/active`);
  }
  getAnnouncements() {
    return this.request(`/setting/user`);
  }

  globalSearchCommunityPostStreams(search) {
    return this.request(`/posts/media/data?search=${search}`);
  }
  fetchSuggestedPilgrim() {
    return this.request(`/users/pilgrim-suggested`);
  }
  followRequest(user_id) {
    return this.request(`/users/${user_id}/follow-request`, { method: "POST" });
  }
  MentorshipRequest(user_id) {
    return this.request(`/users/${user_id}/mentor-request`, { method: "POST" });
  }
  fetchPendingRequests(page = 1, limit = 10) {
    return this.request(`/users/pending-request?page=${page}&limit=${limit}`);
  }
  acceptFollowRequest(user_id) {
    return this.request(`/users/${user_id}/accept-follow-request`, { method: "POST" });
  }
  acceptJoinRoomRequest(room_id, user_id) {
    return this.request(`/users/${user_id}/${room_id}/accept-join-room-request`, { method: "POST" });
  }
  acceptMentorshipRequest(user_id) {
    return this.request(`/users/${user_id}/accept-mentor-request`, { method: "POST" });
  }
  fetchConnectedContact(search = "", page = 1, limit = 10) {
    return this.request(`/users/connected-users?keyword=${search}&page=${page}&limit=${limit}`);
  }
  createLiveRoom(formData) {
    return this.request(`/live_room`, { method: "POST", body: formData });
  }
  getLiveRoomById(roomId) {
    return this.request(`/live_room/${roomId}`);
  }
  updateLiveRoom(id, formData) {
    return this.request(`/live_room/${id}`, { method: "PUT", body: formData });
  }
  joinLiveRoom(data) {
    return this.request(`/live_room/join`, { method: "POST", body: JSON.stringify(data) });
  }
  markMessagesRead(chat_id) {
    return this.request(`/message/${chat_id}/read`, { method: "PUT" });
  }
  markRoomRead(room_id) {
    return this.request(`/live_room/${room_id}/read`, { method: "PUT" });
  }
}

const api = new ApiService();
export default api;